import { respCodeCheck } from "@/04-drivers/dal/error";
import { Editor } from "@tiptap/react";
export const aiChatFetchServer = async <T>(
  url: string,
  data: Record<string, any>,
): Promise<T> => {
  const res = await fetch(`/api/v3/additional/service/${url}`, {
    method: "POST",
    body: JSON.stringify(data),
    headers: {
      "content-type": "application/json",
    },
  });
  const resJson = await res.json();
  respCodeCheck(resJson.base_resp);
  return resJson.data;
};

export const startTag = "<think>";
export const endTag = "</think>";

/**
 * 提取字符串中 <think>...</think> 的内容和其余内容
 * @param {string} str - 原始字符串
 * @returns {{ thinkText: string, answerText: string }}
 */
export function extractThinkAndAnswerText(str: string) {
  if (str.startsWith(startTag)) {
    const start = startTag.length;
    const end = str.indexOf(endTag);

    if (end !== -1) {
      const thinkText = str.slice(start, end);
      const answerText = str.slice(end + endTag.length);
      return { thinkText, answerText };
    }
  }

  return { thinkText: "", answerText: str };
}

/**
 * 替换 mention为name
 */
export const replaceMentionsWithNames = (text: string) =>
  // 正则表达式匹配 [@{...}] 格式的字符串
  text.replace(/\[@(\{.*?\})\]/g, (match: string, jsonStr: string) => {
    try {
      const mentionObj = JSON.parse(jsonStr);
      // 返回name属性值
      return mentionObj.name || match;
    } catch (e) {
      console.error("解析JSON失败:", e);
      return match;
    }
  });

/**
 * 解析输入内容
 * @param editor
 * @param inputContent
 */
export const parseInputContent = (editor: Editor, inputContent: string) => {
  if (!editor) return;
  // 解析文本中的方括号内容并应用特殊样式
  const regex = /\[#(.*?)\]/g;
  let match;
  let lastIndex = 0;
  let text = inputContent;
  let fragments = [];

  while ((match = regex.exec(text)) !== null) {
    // 添加方括号前的普通文本
    if (match.index > lastIndex) {
      fragments.push({
        type: "text",
        text: text.substring(lastIndex, match.index) + " ",
      });
    }

    // 添加自定义提及节点
    const bracketContent = match[0];
    const placeholderText = match[1];

    fragments.push({
      type: "text",
      text: " @",
    });

    lastIndex = match.index + bracketContent.length;
  }

  // 添加剩余文本
  if (lastIndex < text.length) {
    fragments.push({
      type: "text",
      text: " " + text.substring(lastIndex),
    });
  }

  const content = {
    type: "doc",
    content: [
      {
        type: "paragraph",
        content: fragments,
      },
    ],
  };
  editor.commands.setContent(content, true);
  // 设置焦点到第一个提及元素上
  setTimeout(() => {
    // 查找 @ 节点
    const mentionPositions: number[] = [];
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === "text" && node.text === " @") {
        mentionPositions.push(pos);
        return false;
      }
      return true;
    });
    if (mentionPositions.length > 0) {
      const firstPos = mentionPositions[0];
      const node = editor.state.doc.nodeAt(firstPos);
      if (node) {
        // 将光标定位到提及元素后面
        editor.commands.setTextSelection(firstPos + node.nodeSize);
        editor.commands.focus();
      } else {
        editor.commands.focus();
      }
    } else {
      editor.commands.focus();
    }
  }, 0);
};
/**
 * 解析包含 at 结构的字符串数据并设置到 editor
 * @param editor
 * @param inputContent - 包含 [@{...}] 格式的字符串
 */
export const parseAtStructureContent = (
  editor: Editor,
  inputContent: string,
) => {
  if (!editor) return;

  // 正则表达式匹配 [@{...}] 格式的字符串
  const regex = /\[@(\{.*?\})\]/g;
  let match;
  let lastIndex = 0;
  let text = inputContent;
  let fragments = [];

  while ((match = regex.exec(text)) !== null) {
    // 添加 @ 前的普通文本
    if (match.index > lastIndex) {
      const beforeText = text.substring(lastIndex, match.index);
      if (beforeText) {
        fragments.push({
          type: "text",
          text: beforeText,
        });
      }
    }

    // 解析 JSON 对象
    try {
      const jsonStr = match[1];
      const mentionObj = JSON.parse(jsonStr);

      // 创建 mention 节点结构
      fragments.push({
        type: "mention-at",
        attrs: {
          id: mentionObj.id || "",
          type: mentionObj.type || "",
          label: mentionObj.name || "",
          region: mentionObj.region || "",
        },
      });
    } catch (e) {
      console.error("解析 at 结构 JSON 失败:", e);
      // 如果解析失败，保留原文本
      fragments.push({
        type: "text",
        text: match[0],
      });
    }

    lastIndex = match.index + match[0].length;
  }

  // 添加剩余文本
  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex);
    if (remainingText) {
      fragments.push({
        type: "text",
        text: remainingText,
      });
    }
  }

  // 如果没有匹配到任何 at 结构，直接设置文本
  if (fragments.length === 0) {
    editor.commands.setContent(inputContent);
    return;
  }

  const content = {
    type: "doc",
    content: [
      {
        type: "paragraph",
        content: fragments,
      },
    ],
  };
  editor.commands.setContent(content, true);

  // 设置焦点到编辑器末尾
  setTimeout(() => {
    editor.commands.focus();
  }, 0);
};
